import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {type Slide, type SlideType} from '#/types/capsule'

type LFn = (m: MessageDescriptor) => string

/** What this block is missing, or `null` when nothing is. */
type Rule = (slide: Slide, l: LFn) => string | null

/**
 * What is stopping this block from being published, or `null` when nothing is.
 *
 * Shared by the Capsule studio and the FlashBook studio, and the rule is the
 * same in both: **a block that would reach the reader blank is not
 * publishable.** A blank block is not a cosmetic problem. In reading mode the
 * reader turns a page and finds nothing on it; in play mode they meet an exercise
 * with no prompt, a dropdown with no options, a table of empty cells where the
 * data should be, or a choice whose answers are all unlabelled. None of that is
 * recoverable by the reader, and all of it is one sentence away from being fixed
 * by the creator — so it is caught here, at publish, and named.
 *
 * Every type the pickers offer has a rule, because the failure mode of a missing
 * one is silent: the block publishes and a page with nothing on it goes out under
 * the creator's name. That is how `true_false` with no answer marked, and a
 * `fill_blanks` with no paragraph, used to get through. Coverage is held to the
 * pickers by `VALIDATED_TYPES` below.
 *
 * The messages are written to be read *in a list* — one line per problem, under
 * the page and block they belong to, as the issues sheet renders them (see
 * `#/components/studio/StudioIssuesDialog`). So each is an instruction that
 * stands on its own: "Every table cell needs its text", not "invalid table".
 */
export function validateSlide(slide: Slide, l: LFn): string | null {
  if (slide.type === 'content') return validateReadingBlock(slide, l)

  const rule = RULES[slide.type]
  // A type this build has no rules for is left alone: refusing it would strand a
  // draft written by an older version, on a rule that does not exist.
  if (!rule) return null
  return rule(slide, l)
}

/**
 * The types this file has a rule for.
 *
 * Kept beside `RULES` so a test can hold the two together with the pickers: a
 * block type offered to creators without a rule here would publish blank, and
 * that is the one mistake this whole file is arranged to make loud.
 */
export const VALIDATED_TYPES: readonly SlideType[] = [
  'video',
  'content',
  'accordion',
  'true_false',
  'single_choice',
  'multiple_choice',
  'image_choice',
  'fill_blank',
  'fill_blanks',
  'matching',
  'ordering',
  'drag_words',
  'find_words',
  'mark_words',
  'complex_fill_blank',
]

const RULES: Partial<Record<SlideType, Rule>> = {
  // A video slide *is* its video: there is no text in it that could be blank.
  // (The capsule's cover is one; FlashBook adds no video blocks.)
  video: () => null,

  accordion: (slide, l) => {
    const items = slide.accordionItems ?? []
    if (items.length === 0) return l(msg`Add at least one section.`)
    if (items.some(i => !i.title.trim())) {
      return l(msg`Every section needs a title.`)
    }
    // A section that opens onto nothing is a blank section: the reader taps it
    // and it expands to an empty pane.
    if (items.some(i => !i.body.trim())) {
      return l(msg`Every section needs its text.`)
    }
    return null
  },

  true_false: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    // The answer is what this block *is*; a true/false question with neither side
    // marked has no correct answer to grade against.
    if (slide.answer !== 'true' && slide.answer !== 'false') {
      return l(msg`Mark the answer: true or false.`)
    }
    return null
  },

  single_choice: (slide, l) => {
    const issue = validateOptions(slide, l)
    if (issue) return issue
    // One choice, one answer: two correct options would mean every answer the
    // reader could give is graded wrong.
    if ((slide.options ?? []).filter(o => o.isCorrect).length > 1) {
      return l(msg`Single choice allows one correct answer.`)
    }
    return null
  },

  multiple_choice: (slide, l) => validateOptions(slide, l),

  image_choice: (slide, l) => {
    const issue = validateOptions(slide, l)
    if (issue) return issue
    // An image choice is a choice of pictures: an option without one prints as an
    // empty tile with a letter in it.
    if ((slide.options ?? []).some(o => !o.imageUrl)) {
      return l(msg`Every answer needs a picture.`)
    }
    return null
  },

  fill_blank: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    const blanks = slide.blanks ?? []
    if (blanks.length === 0) return l(msg`Add at least one blank.`)
    if (blanks.some(b => !b.answer.trim())) {
      return l(msg`Every blank needs its answer.`)
    }
    return null
  },

  fill_blanks: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    const paragraph = slide.fillBlanksParagraph ?? ''
    if (!paragraph.trim()) return l(msg`Write the paragraph.`)
    const gaps = countBlanks(paragraph)
    if (gaps === 0) return l(msg`Mark at least one blank in the paragraph.`)
    const answers = slide.fillBlanksAnswers ?? []
    if (answers.length !== gaps) {
      return l(msg`Write an answer for each of the blanks.`)
    }
    if (answers.some(a => !a.answer.trim())) {
      return l(msg`Every blank needs its answer.`)
    }
    return null
  },

  matching: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    const pairs = slide.pairs ?? []
    if (pairs.length < 2) return l(msg`Add at least two pairs.`)
    if (pairs.some(p => !p.left.trim() || !p.right.trim())) {
      return l(msg`Every pair needs both of its sides.`)
    }
    return null
  },

  ordering: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    const items = slide.orderItems ?? []
    if (items.length < 2) return l(msg`Add at least two items.`)
    if (items.some(i => !i.label.trim())) {
      return l(msg`Every item needs its text.`)
    }
    return null
  },

  drag_words: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    const template = slide.dragWordsTemplate ?? ''
    if (!template.trim()) return l(msg`Write the paragraph.`)
    if (!template.includes('[')) {
      return l(msg`Mark at least one blank in the paragraph.`)
    }
    const bank = slide.dragWordsBank ?? []
    if (bank.length === 0) return l(msg`Add at least one word to the bank.`)
    if (bank.some(w => !w.trim())) {
      return l(msg`Every word in the bank needs its text.`)
    }
    return null
  },

  find_words: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    const targets = slide.findWordsTargets ?? []
    if (targets.length === 0) return l(msg`Add at least one word to find.`)
    if (targets.some(w => !w.trim())) {
      return l(msg`Every word to find needs its text.`)
    }
    if ((slide.findWordsGrid ?? []).length === 0) {
      return l(msg`Generate the word grid before publishing.`)
    }
    return null
  },

  mark_words: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    if (!slide.markWordsText?.trim()) return l(msg`Write the paragraph.`)
    if ((slide.markWordsCorrect ?? []).length === 0) {
      return l(msg`Mark at least one word in the paragraph.`)
    }
    return null
  },

  complex_fill_blank: (slide, l) => {
    const prompt = missingQuestion(slide, l)
    if (prompt) return prompt
    const blanks = slide.complexBlanks ?? []
    if (blanks.length === 0) return l(msg`Add at least one blank.`)
    if (blanks.some(b => !b.sentence.trim())) {
      return l(msg`Every blank needs its sentence.`)
    }
    if (blanks.some(b => !b.sentence.includes('___'))) {
      return l(msg`Every sentence needs a ___ where the answer goes.`)
    }
    if (blanks.some(b => !b.answer.trim())) {
      return l(msg`Every blank needs its answer.`)
    }
    if (blanks.some(b => b.options.length < 2)) {
      return l(msg`Every blank needs at least two options.`)
    }
    if (blanks.some(b => b.options.some(o => !o.trim()))) {
      return l(msg`Every option needs its text.`)
    }
    return null
  },
}

/**
 * What a *reading* block needs.
 *
 * The rich reading blocks share one `content` type and one editor, so they are
 * told apart by what they present themselves as, and checked by what that
 * presentation actually draws: a quote is its passage, a note is its text, a
 * figure is its picture or its caption, a table is its cells, a formula is its
 * notation, a plain block is its title or its prose.
 */
function validateReadingBlock(slide: Slide, l: LFn): string | null {
  const kind = slide.contentKind ?? 'text'

  if (kind === 'table') {
    const rows = slide.tableRows ?? []
    const filled = rows.some(row => row.some(cell => !!cell.trim()))
    if (!filled) return l(msg`Fill in the table.`)
    // A blank cell in a printed table is a hole in the data, and the first row is
    // the header: a column with no name has no name for the reader either.
    if (rows.some(row => row.some(cell => !cell.trim()))) {
      return l(msg`Every table cell needs its text.`)
    }
    return null
  }

  if (kind === 'formula') {
    if (!slide.latex?.trim()) return l(msg`Write the formula.`)
    return null
  }

  if (kind === 'caption') {
    if (!slide.imageUrl && !slide.contentText?.trim()) {
      return l(msg`Add a picture or a caption.`)
    }
    return null
  }

  if (kind === 'callout') {
    // The label is the note's heading and is optional; the body is the note.
    if (!slide.contentText?.trim()) return l(msg`Write what the note says.`)
    return null
  }

  if (kind === 'quote') {
    if (!slide.contentText?.trim()) return l(msg`Write the quotation.`)
    return null
  }

  if (!slide.contentTitle?.trim() && !slide.contentText?.trim()) {
    return l(msg`Add a title or some text.`)
  }
  return null
}

/**
 * The prompt, for the exercise types that have one.
 *
 * An exercise without a question cannot be answered: the reader is shown
 * options, a grid or a bank, and nothing that says what to do with them.
 */
function missingQuestion(slide: Slide, l: LFn): string | null {
  return slide.question?.trim() ? null : l(msg`Write the question.`)
}

/** A choice's answers: at least two, all labelled, and none of them repeated. */
function validateOptions(slide: Slide, l: LFn): string | null {
  const prompt = missingQuestion(slide, l)
  if (prompt) return prompt
  const opts = slide.options ?? []
  if (opts.length < 2) return l(msg`Add at least two answers.`)
  const labels = opts.map(o => o.label.trim())
  if (labels.some(label => !label)) return l(msg`Every answer needs its text.`)
  if (new Set(labels).size !== labels.length) {
    return l(msg`Two answers have the same text.`)
  }
  if (!opts.some(o => o.isCorrect)) {
    return l(msg`Mark which answer is correct.`)
  }
  return null
}

/** How many `___` gaps a paragraph marks. */
function countBlanks(paragraph: string): number {
  return paragraph.split('___').length - 1
}
